// What a day already has, on the two steps whose entries ADD rather than
// correct: a delivery and a feed batch are events, and a farm can have two of
// each in a day, so typing a quantity always records one more. Without seeing
// what was saved, a supervisor re-opening the day could not tell whether this
// morning's 1,000 kg of maize went in, and typing it again doubled the stock.

export interface SavedLine {
  id: number;
  label: string;
  qty: string;
}

/** null = not loaded yet (or no date chosen); failed = the load itself failed;
 *  more = saved entries beyond the page that was read (the list API caps a
 *  page), named rather than silently left off a list that exists to be complete. */
export type SavedDay = { lines: SavedLine[]; failed: boolean; more: number } | null;

interface Props {
  saved: SavedDay;
  /** "received" / "prepared" — what the lines are. */
  verb: string;
  /** What typing below does: "another delivery" / "another batch". */
  adds: string;
}

export default function AlreadySaved({ saved, verb, adds }: Props) {
  if (!saved) return null;
  if (saved.failed) {
    return (
      <div
        role="alert"
        className="mb-4 rounded-lg border border-warning-200 bg-warning-50 px-4 py-2.5 text-xs text-warning-700 dark:border-warning-800 dark:bg-warning-900/20 dark:text-warning-400"
      >
        Couldn't check what is already {verb} for this day. Look on the Stock page before
        entering anything here, or it may be recorded twice.
      </div>
    );
  }
  if (saved.lines.length === 0) return null;
  return (
    <div
      data-testid={`already-${verb}`}
      className="mb-4 rounded-lg border border-gray-200 bg-gray-50 px-4 py-2.5 text-xs text-gray-700 dark:border-gray-700 dark:bg-gray-800/40 dark:text-gray-300"
    >
      <p className="mb-1 font-medium">Already {verb} for this day:</p>
      <ul className="mb-1 space-y-0.5">
        {saved.lines.map((l) => (
          <li key={l.id}>
            {l.label} — {l.qty}
          </li>
        ))}
        {saved.more > 0 && <li>…and {saved.more} more (see the Stock page)</li>}
      </ul>
      <p className="text-gray-500 dark:text-gray-400">
        These are saved. Enter a quantity below only for {adds}; it is added on top.
      </p>
    </div>
  );
}
